import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import DialogContentText from '@mui/material/DialogContentText';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import {
    CollectionEditDocument,
    UpdateCollectionDocument,
    DeleteCollectionDocument,
    ITrainingListItemFragment,
    ICollectionGroupFragment,
    IUpdateLibraryCollectionInput,
} from 'graphql/types';
import { Button } from 'common/components/Button';
import { PageDrawer } from 'common/components/PageDrawer';
import { LibraryCollectionForm } from 'library/components/forms/LibraryCollectionForm';
import { ApolloError } from 'common/components/ApolloError';
import { Loader } from 'common/components/Loader';
import { AlertDialog } from 'common/components/AlertDialog';
import { useSnackbar } from 'common/hooks/useSnackbar';
import { useApolloError } from 'common/hooks/useApolloError';
import { PageTitle } from 'common/components/PageTitle';
import { getImageId } from 'common/utils/image';
import { formatColor } from 'common/utils/color';

interface IProps {
    pageTitle?: string;
    id?: string;
    open?: boolean;
    onUpdate(): void;
    onDelete(): void;
    onClose?(): void;
}

export const LibraryCollectionDrawer = ({
    pageTitle,
    id,
    open,
    onUpdate,
    onDelete,
    onClose,
}: IProps) => {
    const [translate] = useTranslation();
    const [displaySnackbar] = useSnackbar();
    const { showApolloError } = useApolloError();
    const [showOnDeleteAlert, setShowOnDeleteAlert] = useState<boolean>(false);
    const [thumbnailId, setThumbnailId] = useState<string>();
    const [headerImageId, setHeaderImageId] = useState<string>();

    const { loading, error, data } = useQuery(CollectionEditDocument, {
        variables: { id },
    });

    useEffect(() => {
        if (!data) return;
        const thumbnail = data?.libraryCollection?.thumbnail?.id;

        if (thumbnail) setThumbnailId(thumbnail);

        const headerImage = data?.libraryCollection?.headerImage?.id;

        if (headerImage) setHeaderImageId(headerImage);
    }, [data]);

    const collection = data?.libraryCollection;

    const [updateCollection, { error: updateError }] = useMutation(
        UpdateCollectionDocument
    );

    const [deleteCollection, { loading: deleteLoading }] = useMutation(
        DeleteCollectionDocument,
        {
            update: (cache) => {
                if (!collection) return;

                // Remove collection from cache
                cache.evict({ id: `LibraryCollection:${id}` });
                cache.gc();
            },
        }
    );

    if (loading || error) {
        return (
            <PageDrawer open={open}>
                {error && <ApolloError error={error} />}
                {loading && <Loader />}
            </PageDrawer>
        );
    }

    if (!collection) return null;

    const {
        title,
        description,
        active,
        access,
        root,
        titleColor,
        thumbnail,
        headerImage,
    } = collection;

    const accessTrainings = (access || []).filter(
        (a) => a.__typename === 'TrainingListItem'
    ) as ITrainingListItemFragment[];

    const accessGroups = (access || [])?.filter(
        (a) => a.__typename === 'Group'
    ) as ICollectionGroupFragment[];

    const initialValues = {
        title,
        description,
        active,
        accessTrainings,
        accessGroups,
        titleColor: titleColor?.replace('#', '') || '',
        thumbnail,
        headerImage,
    };

    const imageUploaded =
        thumbnailId !== data?.libraryCollection?.thumbnail?.id ||
        headerImageId !== data?.libraryCollection?.headerImage?.id;

    return (
        <PageDrawer open={open} onClose={onClose}>
            {updateError && <ApolloError error={updateError} />}

            {pageTitle && (
                <PageTitle>{`${pageTitle} - ${translate('editCollection')}`}</PageTitle>
            )}

            <Box sx={{ p: { xs: 2, sm: 4 } }}>
                <Box sx={{ mb: 4 }}>
                    <Typography sx={{ fontWeight: 'bold', mt: 0 }} variant="h3">
                        {title}
                    </Typography>
                </Box>

                <LibraryCollectionForm
                    imageUploaded={imageUploaded}
                    initialValues={initialValues}
                    isDeleting={deleteLoading}
                    isRootCollection={root}
                    onClose={onClose}
                    onDelete={() => {
                        setShowOnDeleteAlert(true);
                    }}
                    onSubmit={(values) => {
                        // Before submitting the collection we want to change accessGroups and accessTraining to a
                        // single array of uid's
                        const {
                            accessGroups,
                            accessTrainings,
                            thumbnail,
                            headerImage,
                            active: newActiveValue,
                            titleColor,
                            ...collectionInput
                        } = values;
                        const {
                            thumbnail: currentThumbnail,
                            headerImage: currentHeaderImage,
                        } = collection || {};

                        const access = [
                            ...accessTrainings.map((training) => training.id),
                            ...accessGroups.map((group) => group.id),
                        ];

                        const updatedCollection = {
                            ...collectionInput,
                            titleColor: formatColor(titleColor),
                            // Only include active field for non-root collections
                            ...(root ? {} : { active: newActiveValue }),
                            thumbnailId: getImageId(
                                currentThumbnail,
                                thumbnail
                            ),
                            headerImageId: getImageId(
                                currentHeaderImage,
                                headerImage
                            ),
                            access,
                        } as IUpdateLibraryCollectionInput;

                        return updateCollection({
                            variables: {
                                id: collection.id,
                                collection: updatedCollection,
                            },
                        }).then(() => {
                            displaySnackbar(
                                translate(
                                    'libraryCollectionActionSuccess.update'
                                ),
                                {
                                    variant: 'success',
                                }
                            );

                            onUpdate();
                        });
                    }}
                />
            </Box>

            <AlertDialog
                actions={
                    <>
                        <Button
                            autoFocus
                            color="error"
                            loading={deleteLoading}
                            variant="contained"
                            onClick={async () => {
                                try {
                                    await deleteCollection({
                                        variables: { id: collection.id },
                                    });
                                } catch (error) {
                                    setShowOnDeleteAlert(false);
                                    showApolloError(error);

                                    return;
                                }

                                setShowOnDeleteAlert(false);

                                displaySnackbar(
                                    translate(
                                        'libraryCollectionActionSuccess.delete'
                                    ),
                                    {
                                        variant: 'success',
                                    }
                                );

                                onDelete();
                            }}
                        >
                            {translate('delete')}
                        </Button>
                        <Button
                            disabled={deleteLoading}
                            variant="contained"
                            onClick={() => {
                                setShowOnDeleteAlert(false);
                            }}
                        >
                            {translate('cancel')}
                        </Button>
                    </>
                }
                open={showOnDeleteAlert}
                title={translate(
                    root
                        ? 'deleteRootCollectionMessage.title'
                        : 'deleteCollectionMessage.title'
                )}
            >
                <DialogContentText color="text.primary" variant="body2">
                    {translate(
                        root
                            ? 'deleteRootCollectionMessage.text'
                            : 'deleteCollectionMessage.text'
                    )}
                </DialogContentText>
            </AlertDialog>
        </PageDrawer>
    );
};
